import { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, XCircle, Loader2, ExternalLink, Users, Monitor, Calendar, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

interface LicenceInfo {
  valid: boolean;
  reason?: string;
  licence_id?: string;
  org_type?: string;
  max_users?: number;
  max_devices?: number;
  features?: Record<string, boolean>;
  valid_until?: string;
  status?: string;
}

interface Props {
  value: string;
  licenceInfo: LicenceInfo | null;
  onChange: (value: string) => void;
  onLicenceValidated: (info: LicenceInfo | null) => void;
}

const REASON_MESSAGES: Record<string, string> = {
  licence_not_found: "Clé de licence introuvable. Vérifiez la saisie.",
  licence_suspended: "Cette licence a été suspendue. Contactez le support.",
  licence_expired: "Cette licence est expirée. Renouvelez votre abonnement.",
  licence_already_used: "Cette licence est déjà utilisée par une autre organisation.",
  rate_limit_exceeded: "Trop de tentatives. Réessayez dans 10 minutes.",
  invalid_input: "Format de clé invalide.",
  server_error: "Erreur serveur. Réessayez dans quelques instants.",
};

const FEATURE_LABELS: Record<string, string> = {
  montage_social: "Montage EFI Sociaux",
  montage_consolide: "Montage EFI Consolidés",
  pointage_efi_pdf: "Pointage E-Liasse PDF",
};

export function StepLicence({ value, licenceInfo, onChange, onLicenceValidated }: Props) {
  const [loading, setLoading] = useState(false);

  const handleValidate = useCallback(async () => {
    if (!value.trim()) return;
    setLoading(true);
    onLicenceValidated(null);

    try {
      const { data, error } = await supabase.functions.invoke("validate-licence", {
        body: { licence_key: value.trim().toUpperCase() },
      });

      if (error) throw error;
      onLicenceValidated(data as LicenceInfo);
    } catch {
      onLicenceValidated({ valid: false, reason: "server_error" });
    } finally {
      setLoading(false);
    }
  }, [value, onLicenceValidated]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleValidate();
    }
  };

  const formatKey = (val: string) => {
    // Auto-format to SMEFI-XXXX-XXXX-XXXX
    const clean = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const parts = clean.match(/.{1,4}/g) || [];
    return parts.slice(0, 4).join("-");
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="licence_key">
          Clé de licence <span className="text-destructive">*</span>
        </Label>
        <p className="text-xs text-muted-foreground">
          Format : <span className="font-mono">SMEFI-XXXX-XXXX-XXXX</span>
        </p>
        <div className="flex gap-2">
          <Input
            id="licence_key"
            value={value}
            onChange={(e) => {
              onChange(formatKey(e.target.value));
              onLicenceValidated(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="SMEFI-XXXX-XXXX-XXXX"
            className="font-mono tracking-widest"
            maxLength={19}
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleValidate}
            disabled={loading || value.length < 4}
            className="shrink-0"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Valider"
            )}
          </Button>
        </div>
      </div>

      {/* Validation result */}
      {licenceInfo && (
        <div className={cn(
          "rounded-lg border p-4 space-y-3 transition-all",
          licenceInfo.valid
            ? "border-success/30 bg-success/5"
            : "border-destructive/30 bg-destructive/5"
        )}>
          <div className="flex items-center gap-2">
            {licenceInfo.valid ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
                <span className="font-medium text-success">Licence valide</span>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-destructive shrink-0" />
                <span className="font-medium text-destructive">
                  {REASON_MESSAGES[licenceInfo.reason || ""] || "Licence invalide"}
                </span>
              </>
            )}
          </div>

          {licenceInfo.valid && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="w-4 h-4 shrink-0" />
                <span>Max {licenceInfo.max_users} utilisateurs</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Monitor className="w-4 h-4 shrink-0" />
                <span>Max {licenceInfo.max_devices} appareils/user</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4 shrink-0" />
                <span>Expire le {new Date(licenceInfo.valid_until!).toLocaleDateString("fr-FR")}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Layers className="w-4 h-4 shrink-0" />
                <span className="capitalize">{licenceInfo.org_type}</span>
              </div>

              {/* Features */}
              {licenceInfo.features && (
                <div className="col-span-2 flex flex-wrap gap-1.5 pt-1">
                  {Object.entries(licenceInfo.features).map(([key, enabled]) => (
                    <Badge
                      key={key}
                      variant={enabled ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {enabled ? "✓" : "✗"} {FEATURE_LABELS[key] || key}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {!licenceInfo.valid && (
            <a
              href="#"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Acheter une licence
            </a>
          )}
        </div>
      )}

      {!licenceInfo && (
        <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
          💡 Vous n'avez pas encore de licence ? Contactez notre équipe commerciale pour en obtenir une.
        </p>
      )}
    </div>
  );
}
